import dayjs from 'dayjs';
import { useMemo } from 'react';
import { formatDate, formatMonthDate } from '../../../../../helpers/date-time-helpers';
import { simpleLinearRegression } from '../../../../../helpers/math-helpers/math-helpers';
import { useProcessAnalysisStore } from '../../../../../store/process-analysis-store/process-analysis-store';
import {
  useSelectedInitiativeCompletions,
  useSelectedInitiativeCompletionTrends,
} from '../../../../../store/process-analysis-store/process-analysis-store.hooks';
import type { ChartDataItem } from '../../../components/line-chart/line-chart.type';
import { MetricType } from '../../../process-analysis.type';

/**
 * Hook to transform initiative completion data for chart consumption
 *
 * @returns {ChartDataItem[]} Chart data items
 */
const useChartData = (): ChartDataItem[] => {
  const data = useProcessAnalysisStore((state) => state.initiativeCompletionData);
  const selectedInitiativeCompletions = useSelectedInitiativeCompletions();
  const selectedInitiativeCompletionTrends = useSelectedInitiativeCompletionTrends();
  const metric = useProcessAnalysisStore((state) => state.initiativeCompletionMetric);

  return useMemo(() => {
    if (!data?.initiatives || !data?.labels) {
      return [];
    }

    // Calculate trend lines for selected initiatives
    const trendCalculations: Record<string, { slope: number; intercept: number }> = {};
    selectedInitiativeCompletionTrends.forEach((selected) => {
      const initiative = data.initiatives.find((init) => init.id === selected.id);
      if (initiative) {
        // Convert initiative metric data to sorted date-value pairs
        const metricData = metric === MetricType.WorkUnits ? initiative.tasks : initiative.points;
        const sortedEntries = Object.entries(metricData).sort(([a], [b]) => a.localeCompare(b));
        const validValues = sortedEntries
          .map(([, value]) => value)
          .filter((value) => value !== null && value !== undefined);

        if (validValues.length >= 2) {
          const xAxis = Array.from({ length: validValues.length }, (_, index) => index);
          const trendResult = simpleLinearRegression(xAxis, validValues);

          if (trendResult) {
            trendCalculations[initiative.id] = trendResult;
          }
        }
      }
    });

    return data.labels
      .map((date, index) => {
        const dateObj = dayjs(date, 'MMM YY');

        if (!dateObj.isValid()) {
          return null;
        }

        const chartDataItem: ChartDataItem = {
          date: formatMonthDate(dateObj),
          rawDate: formatDate(dateObj),
        };

        data.initiatives.forEach((initiative) => {
          const isSelectedForDisplay = selectedInitiativeCompletions.some((selected) => selected.id === initiative.id);
          const isSelectedForTrend = selectedInitiativeCompletionTrends.some(
            (selected) => selected.id === initiative.id,
          );
          const dateKey = formatDate(dateObj, { format: 'YYYY-MM' });

          if (isSelectedForDisplay) {
            const metricData = metric === MetricType.WorkUnits ? initiative.tasks : initiative.points;
            const value = metricData[dateKey];
            if (value !== undefined) {
              chartDataItem[initiative.id] = value;
            }
          }

          if (isSelectedForTrend) {
            const trendCalc = trendCalculations[initiative.id];
            if (trendCalc) {
              chartDataItem[`${initiative.id}_trend`] = trendCalc.slope * index + trendCalc.intercept;
            }
          }
        });

        return chartDataItem;
      })
      .filter((item): item is ChartDataItem => item !== null)
      .sort((a, b) => dayjs(a.rawDate).diff(dayjs(b.rawDate)));
  }, [data, selectedInitiativeCompletions, selectedInitiativeCompletionTrends, metric]);
};

export { useChartData };
